import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withRouter, RouteComponentProps } from 'react-router';
import { useSelector } from 'react-redux';
import tStore from 'types/store';
import {
  Spinner,
  PageWrapper,
  ComplexTable,
  Breadcrumbs,
  Tiles,
  ListTiles,
  ListTitle,
} from 'components';
import { tItsStructureList } from 'types/services/itsStructure';
import { tGetAllUsersParams } from 'types/services/users';
import { useFetch } from 'hooks';
import itsStructureService from 'services/itsStructure';
import { ListBreadcrumb, ListTableContainer } from 'styles/GlobalStyledComponents';
import { tableHead, searchableFields } from './TableConfig';

interface iItsStructureList extends RouteComponentProps<any> {}

const ItsStructureList: React.FC<iItsStructureList> = ({ history }): JSX.Element => {
  const { t } = useTranslation();

  const permissions = useSelector((state: tStore) => state.user.permissions);
  const canWrite = !!permissions.includes('tunEmployeesWrite');

  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [search, setSearch] = useState<string>('');

  const { fetch, loading, data, count } = useFetch<tItsStructureList, tGetAllUsersParams>(
    itsStructureService.getAll,
  );

  const fetchData = useCallback(
    () => fetch({ search, searchFields: searchableFields, limit, offset, kind: 'own' }),
    [search, searchableFields, limit, offset, fetch],
  );

  const setRequestParams = useCallback(
    (newOffset: number, newLimit: number) => {
      if (newOffset !== offset) setOffset(newOffset);
      if (newLimit !== limit) setLimit(newLimit);
    },
    [limit, offset],
  );

  useEffect(() => {
    setOffset(0);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [limit, offset, fetchData]);

  const createNew = () => history.push('/its-structure/new');

  const showDetails = (id: number | string) => history.push(`/its-structure/${id}`);

  return (
    <PageWrapper>
      {loading && <Spinner />}
      <ListBreadcrumb>
        <Breadcrumbs />
      </ListBreadcrumb>
      <ListTitle title={t('ITS Structure')} />
      <ListTiles>
        <Tiles
          text={t('Create a new ITS Employee')}
          icon='personAdd'
          bgIcon='addComment'
          color='orange'
          variant='filled'
          onClick={createNew}
          disabled={!canWrite}
        />
      </ListTiles>
      <ListTableContainer>
        <ComplexTable
          isRowClickable
          onRowClick={showDetails}
          value={search}
          resetValue={() => setSearch('')}
          onChange={({ target: { value } }) => setSearch(value)}
          name='tableSearch'
          tableBodyData={data}
          tableHead={tableHead}
          totalRecords={count}
          setParams={(offset: number, limit: number) => setRequestParams(offset, limit)}
          tableName='List of ITS employees'
          limit={limit}
          offset={offset}
        />
      </ListTableContainer>
    </PageWrapper>
  );
};

export default withRouter(ItsStructureList);
